import Enumerable from "linq";
import { useCallback } from "react";
import { useQuery } from "react-query";
import { UIViewEnum } from "../../common/enums/UIViewEnum";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useUserContext } from "../../contexts/user-context/UserContext";
import NavigationDataService from "../../services/navigation-data-service/NavigationDataService";
import { BaseUseQueryParameters } from "../BaseUseQueryParameters";
import { keys } from "./Keys";
import { RestError } from "../../services/rest-service/RestService";
import { IeLynxObject } from "../../common/models/eLynxObject";

export type SummaryListingTabsType = {
    hasDevices: boolean;
    hasWells: boolean;
    hasTankBatteries: boolean;
    hasAlarmLynx: boolean;
    hasCompressorLynx: boolean;
};

export type UseAvailableSummaryListingTabsParameters = {
    contextObject?: IeLynxObject;
    contextGroupID?: number;

} & BaseUseQueryParameters<SummaryListingTabsType | undefined>;

export function useAvailableSummaryListingTabs({
    contextGroupID,
    contextObject,
    enabled,
    onError,
    onSuccess,
    refreshSeconds,
}: UseAvailableSummaryListingTabsParameters) {
    const [userState] = useUserContext();
    const [offlineState] = useOfflineDetectContext();
    const routeContext = useRouteContext();

    const customerID = userState.customer?.id as number;
    const groupID = contextGroupID ?? routeContext.groupID as number;
    const eLynxObject = contextObject ?? routeContext.contextObject;
    const offline = offlineState.offline;

    return useQuery<Array<UIViewEnum>, RestError, SummaryListingTabsType>(
        keys.list(customerID, groupID, eLynxObject, offline),
        ({ signal }) => {
            return NavigationDataService.getAvailableViews(
                customerID,
                groupID,
                eLynxObject as IeLynxObject,
                offline ?? false,
                signal
            );
        },
        {
            select: useCallback(
                (data: Array<UIViewEnum>) => select(data),
                []
            ),
            enabled,
            onError: onError,
            onSuccess: onSuccess,
            refetchInterval: refreshSeconds ? refreshSeconds  * 1000 : false
        }
    );
}

function select(views: Array<UIViewEnum>) {
    const viewMap = Enumerable.from(views).toDictionary(view => view);

    return {
        hasDevices: viewMap.contains(UIViewEnum.Group_SummaryListing_Devices),
        hasWells: viewMap.contains(UIViewEnum.Group_SummaryListing_Wells),
        hasTankBatteries: viewMap.contains(UIViewEnum.Group_SummaryListing_TankBatteries),
        hasAlarmLynx: viewMap.contains(UIViewEnum.Group_SummaryListing_AlarmLynx),
        hasCompressorLynx: viewMap.contains(UIViewEnum.Group_SummaryListing_CompressorLynx)
    } as SummaryListingTabsType;
}